import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  ControlButton,
  Controls,
  Edge,
  EdgeTypes,
  FitViewOptions,
  MiniMap,
  Node,
  NodeChange,
  NodeTypes,
  PanelPosition,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodes,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
  XYPosition,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import classNames from 'classnames';
import { ExternalTriggerProvider } from 'js/components/pipelines/cards/components/ExpandActions/context/ExternalTriggerContext';
import { ExternalTriggerUsage } from 'js/components/pipelines/cards/components/ExpandActions/ExternalTriggerUsage';
import { ApiServerNode } from 'js/components/pipelines/FlowNodes/components/ApiServerNode';
import { CredentialNode } from 'js/components/pipelines/FlowNodes/components/CredentialNode';
import AnimatedConnectionLine from 'js/components/pipelines/FlowNodes/components/edges/AnimatedConnectionLine';
import CurvedEdge from 'js/components/pipelines/FlowNodes/components/edges/CurvedEdge';
import ExpandHandlerProvider from 'js/components/pipelines/FlowNodes/components/ExpandHandlerProvider';
import FlowNode from 'js/components/pipelines/FlowNodes/components/FlowNode';
import { HandleMenu } from 'js/components/pipelines/FlowNodes/components/HandleMenu';
import NexsetLoaderNode from 'js/components/pipelines/FlowNodes/components/NexsetLoaderNode';
import CodeNode from 'js/components/pipelines/FlowNodes/components/CodeNode';
import ByoSourceShellNode from 'js/components/pipelines/FlowNodes/components/ByoSourceShellNode';
import ByoDestShellNode from 'js/components/pipelines/FlowNodes/components/ByoDestShellNode';
import { ProjectNode } from 'js/components/pipelines/FlowNodes/components/ProjectNode';
import { RAGNode } from 'js/components/pipelines/FlowNodes/components/RAGNode';
import { ResourceCountNode } from 'js/components/pipelines/FlowNodes/components/ResourceCountNode';
import ResourceNode from 'js/components/pipelines/FlowNodes/components/ResourceNode';
import { ResourceOrchestrationProvider } from 'js/components/pipelines/FlowNodes/components/ResourceOrchestration';
import { VirtualNode } from 'js/components/pipelines/FlowNodes/components/VirtualNode';
import { useExpandedNodes } from 'js/components/pipelines/FlowNodes/context/ExpandContext';
import useOrchestration from 'js/components/pipelines/FlowNodes/hooks/useOrchestration';
import { useZoomState } from 'js/components/pipelines/FlowNodes/hooks/useZoomState';
import { mapToNodes } from 'js/components/pipelines/FlowNodes/mappers';
import { LAYOUT, Z_INDEX } from 'js/components/pipelines/FlowNodes/constants';
import {
  isExcludedFlowType,
  resolveEffectiveInterval,
  getMetricsIntervalOptions,
} from 'js/constants/metricsConstants';
import { isAllowedEmbeddedParentOrigin } from 'js/constants/config';
import ResourceEditProvider from 'js/components/ResourceEditProvider';
import { ResourceEditContext } from 'js/components/ResourceEditProvider/ResourceEditContext';
import { useNodeCreationActions } from './hooks/useNodeCreationActions';
import DataSetSplitDialog from 'new/components/SplitDataSet/DataSetSplitDialog';
import { isEqual } from 'lodash';
import {
  ApiRequestModalProvider,
  useApiRequestModal,
} from 'new/components/flows/types/api_server/context/ApiRequestModal/ApiRequestModalContext';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { ResourceDetailProvider } from 'new/components/ResourceDetailHandler/ResourceDetailProvider';
import { useResourceDetailHandler } from 'new/components/ResourceDetailHandler/context';
import { useAppContext } from 'new/hooks/useAppContext';
import { useCreateApiRequest } from 'new/hooks/useCreateApiRequest';

import useEntities from 'new/state/entities/repository/useEntities';
import usePaging from 'new/state/paging/repository/usePaging';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import React, {
  CSSProperties,
  forwardRef,
  memo,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { ApiServerHelpProvider } from '../cards/components/ExpandActions/context/ApiServerHelpContext';
import DraggableGenAiChatNode from './components/DraggableGenAiChatNode';
import { CronJobNode } from './components/CronJobNode';
import { LastRunNode } from './components/LastRunNode';
import { SecondaryFlowNode } from './components/SecondaryFlowNode';
import { createEdges } from './createEdges';
import { applyDagreLayoutToResources } from './dagreLayout';
import classes from './FlowNodes.module.scss';
import { mergeNodesPosition } from './helpers';
import useGenAiChat from './hooks/useGenAiChat';
import useInitialFitView from './hooks/useInitialFitView';
import { placeFlowInViewport } from './placeFlowInViewport';
import type { LayoutDirection } from './types';
import { PipelineItems } from './types';
import { updateNodesWithControlNodes } from './utils';
import {
  MetricsIntervalSelector,
  MetricsInterval,
} from 'new/elements/MetricsIntervalSelector/MetricsIntervalSelector';
import { useMetricsAdapter } from 'new/state/metrics/adapter/useMetricsAdapter';
import { setFlowMetricsInterval } from 'js/actions';
import { getFlowMetricsInterval } from 'js/reducers/metrics/selectors';
import useAppDispatch from 'js/hooks/useAppDispatch';

export type ToolbarItem = {
  Icon: ReactNode;
  title?: string;
  onClick: (id: any) => void;
};

interface FlowNodesProps {
  id: string;
  hideMiniMap?: boolean;
  disableHashing?: boolean;
  miniMapPosition?: PanelPosition;
  miniMapStyle?: CSSProperties;
  triggeringMap?: any;
  linksMap?: any;
  fitViewOptions?: FitViewOptions;
  pipelineItems: PipelineItems;
  flowsOrder?: number[];
  keepFlowsOrder?: boolean;
  projects?: any[];
  nodeProps?: any;
  toolbarItems?: ToolbarItem[];
  isFlowExpanded?: boolean;
  isApiServerFlow?: boolean;
  isSqlAutomation?: boolean;
  excludeFlowControlNode?: boolean;
  /** Layout direction: 'LR' (left-to-right) or 'TB' (top-to-bottom). Default 'LR'. */
  layoutDirection?: LayoutDirection;
}

const proOption = {
  hideAttribution: true,
};

const types = {
  resource: ResourceNode,
  data_source: ResourceNode,
  data_set: ResourceNode,
  data_sink: ResourceNode,
  splitter: ResourceNode,
  credential_group: CredentialNode,
  flow: FlowNode,
  secondary_flow: SecondaryFlowNode,
  resourceCount: ResourceCountNode,
  project: ProjectNode,
  nexsetLoader: NexsetLoaderNode,
  // BYO (custom) flow: synthetic node rendered in place of the nexset.
  code: CodeNode,
  // BYO create flow: dashed SOURCE shell before a real source is configured.
  byoSourceShell: ByoSourceShellNode,
  // BYO flow: independent destination shell node (no sink yet).
  byoDestShell: ByoDestShellNode,
  // RAG flow nodes
  query_prompt: RAGNode,
  data_retriever_holder: RAGNode,
  query_rewriter: RAGNode,
  context_enricher: RAGNode,
  llm_processor: RAGNode,
  reranker: RAGNode,
  query_response: RAGNode,
  last_run: LastRunNode,
  cron_job: CronJobNode,
  // API flow nodes
  api_server: ApiServerNode,
  api_request: ApiServerNode,
  api_success_modifier: ApiServerNode,
  splitter_output: ApiServerNode,
  api_target: ApiServerNode,
  api_result: ApiServerNode,
  api_error: ApiServerNode,
  api_error_modifier: ApiServerNode,
  // Virtual node for temporary connections
  virtual: VirtualNode,
} as NodeTypes;

const edgeTypes = {
  curved: CurvedEdge,
} as EdgeTypes;

const minMapDefaultStyle = {
  backdropFilter: 'blur(12px)',
  borderRadius: 8,
  border: '1px solid var(--color-outline)',
  background: 'none',
  marginBottom: 50,
};

const FlowNodesInternal = forwardRef(
  (
    props: PropsWithChildren<
      FlowNodesProps & {
        onClickOrchestrate?: () => void;
      }
    >,
    ref,
  ) => {
    const {
      children,
      pipelineItems,
      flowsOrder,
      nodeProps = {},
      fitViewOptions,
      triggeringMap,
      miniMapPosition = 'bottom-right',
      miniMapStyle,
      isFlowExpanded,
      keepFlowsOrder,
      hideMiniMap,
      linksMap,
      isApiServerFlow,
      isSqlAutomation,
      layoutDirection = 'LR',
    } = props;
    const { mode, isEmbeddedMode } = useAppContext();
    const flowNodes = useSelector((state: any) => state.entities.flowNodes);
    const vendors = useSelector((state: any) => state.entities.vendors);
    const branches = useSelector((state: any) => state.pipelines.branches);
    const { data: entities } = useEntities();
    const { screenToFlowPosition, fitView } = useReactFlow();
    const updateNodeInternals = useUpdateNodeInternals();
    const ragFlowId = useRagFlowId();
    const [nodes, setNodes] = useNodesState<any>([]);
    const [edges, setEdges] = useEdgesState([]);
    const [changedNodes, setChangedNodes] = useState<{
      [key: string]: { flowNode: any; position: XYPosition };
    }>({});
    /**
     * ISSUE: Edge Rendering Race Condition
     *
     * Problem: Edges were being created and set before their target nodes existed in the DOM.
     * This caused edges to appear with invalid targets (e.g., 'node-undefined') or edges
     * would fail to render because React Flow couldn't find the target node.
     *
     * Solution: Implement a two-phase rendering approach:
     * 1. Store edges in a ref (pendingEdgesRef) instead of setting them immediately
     * 2. Use a separate effect that runs after nodes are confirmed to be rendered
     * 3. Validate edges before setting them to ensure both source and target nodes exist
     */
    const pendingEdgesRef = useRef<Edge[]>([]);
    // Track if we're currently setting edges to prevent infinite loops
    const isSettingEdgesRef = useRef(false);
    // Track the last edges we set to avoid unnecessary updates
    const lastEdgesRef = useRef<Edge[]>([]);
    const dispatch = useAppDispatch();
    const { refreshPipelineById } = usePipelinesAdapter();
    const { requestFlowMetrics } = useMetricsAdapter();
    const metricsInterval = useSelector(getFlowMetricsInterval) as MetricsInterval;
    const flowElementRef = useRef<HTMLDivElement>(null);
    const {
      isGenAiChatOpen,
      selectedNexsetIdForGenAiChat,
      handleOpenGenAiChat,
      originNodeId,
      handleCloseGenAiChat,
    } = useGenAiChat();
    // Context menu state for handle drag-to-empty-space
    const [contextMenu, setContextMenu] = useState<null | {
      position: { x: number; y: number };
      sourceHandle: string;
      sourceNode: string;
    }>(null);

    // Track drag source information (using ref to avoid timing issues)
    const dragSourceRef = useRef<null | {
      nodeId: string;
      handleId: string;
    }>(null);

    // Track temporary node and edge for menu interactions
    const [tempNode, setTempNode] = useState<null | Node>(null);
    const [tempEdge, setTempEdge] = useState<null | Edge>(null);

    /**
     * ISSUE: Infinite Loop in Node Position Tracking
     *
     * Problem: The onNodesChange callback was causing infinite re-render loops when tracking
     * position changes. This happened because:
     * - setNodes() updates React Flow state
     * - React Flow triggers onNodesChange again
     * - setChangedNodes() causes a re-render
     * - The cycle repeats indefinitely
     *
     * Solution: Use a ref flag to prevent re-entry into the callback while processing changes.
     * This breaks the cycle by ensuring we only process one batch of changes at a time.
     */
    const isProcessingNodesChangeRef = useRef(false);

    const fitted = useInitialFitView(flowElementRef.current, layoutDirection);

    const { expandedNodes, expandedSources } = useExpandedNodes();
    const { onClickOrchestrate } = useOrchestration();

    const {
      paging: { metaInfoById },
      getMetaInfoById,
    } = usePaging();

    // `?select=` (see SourcePipeline): open that node's detail panel, the same one the
    // card's Details button opens, as soon as the node is on the canvas.
    const { openDetailDrawerInPortal } = useResourceDetailHandler();
    const { selectedNodeKey, onSelectedNodeOpened } = nodeProps;
    useEffect(() => {
      if (selectedNodeKey == null) return;
      const item = nodes.find(
        (node) =>
          ['SOURCE', 'DATA_SET', 'DATA_SINK'].includes(node.data?.item?.type) &&
          String(node.data?.nodeKey) === selectedNodeKey,
      )?.data?.item;
      if (!item) return;
      openDetailDrawerInPortal(item, getMetaInfoById(item.connector_type));
      onSelectedNodeOpened?.();
    }, [nodes, selectedNodeKey, onSelectedNodeOpened, openDetailDrawerInPortal, getMetaInfoById]);

    const flowInstance = useReactFlow();
    const prevLayoutDirectionRef = useRef<typeof layoutDirection>(layoutDirection);
    const layoutDirectionJustChangedRef = useRef(false);
    const handleUpdateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useImperativeHandle(ref, () => flowInstance);

    const { isZoomedOut } = useZoomState();

    // When layout direction changes, mark that we need to sync and fit view after nodes update
    useEffect(() => {
      if (prevLayoutDirectionRef.current !== layoutDirection) {
        prevLayoutDirectionRef.current = layoutDirection;
        layoutDirectionJustChangedRef.current = true;
      }
    }, [layoutDirection]);

    // Cleanup handle-update timer on unmount
    useEffect(() => {
      return () => {
        if (handleUpdateTimerRef.current) clearTimeout(handleUpdateTimerRef.current);
      };
    }, []);

    /**
     * Credential-edge measurement sync.
     *
     * On an initial TB paint of a flow with dependent/linked flows, the
     * credential node's measured height is not available when dagre lays out
     * the canvas. `repositionCredentialNodes` falls back to an estimate, so
     * React Flow routes the credential→source edge against positions that
     * stop matching once the credential actually measures. The edge then
     * renders along a dead path and looks invisible until the user toggles
     * direction (which triggers a full re-layout with fresh measurements).
     *
     * Once a credential node becomes measured for the first time, call
     * updateNodeInternals on it and its paired source so React Flow re-
     * evaluates the handle positions and the edge paints. Ref-tracked so we
     * only pay the cost once per credential node per session.
     */
    const measurementNodes = useNodes();
    const syncedCredIdsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
      for (const node of measurementNodes) {
        const id = node.id as string;
        if (!id.startsWith('credential-')) continue;
        if (syncedCredIdsRef.current.has(id)) continue;
        if (!node.measured?.height) continue;

        syncedCredIdsRef.current.add(id);
        updateNodeInternals(id);
        const sourceId = id.replace('credential-', 'node-');
        updateNodeInternals(sourceId);
      }
    }, [measurementNodes, updateNodeInternals]);

    const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

    const aclQueryMode = useSelector((state: any) => state.app.listing.aclQueryMode);

    const ragRequests = useSelector((state: any) => state.rag.queryRequest);

    // Handle postMessage for AGENT_MESSAGE_COMPLETED to trigger reload
    useEffect(() => {
      // Only set up postMessage listener in embedded mode
      if (!isEmbeddedMode) {
        return;
      }

      const handleAgentMessage = (event: MessageEvent) => {
        if (!isAllowedEmbeddedParentOrigin(event.origin)) {
          return;
        }

        // Check if this is our agent completion message
        if (event.data?.type !== 'AGENT_MESSAGE_COMPLETED') {
          return;
        }

        // Simulate pressing the reload button by refreshing the pipeline
        if (isFlowExpanded && originNodeId) {
          // If we're in expanded view and have an origin node, refresh that specific pipeline
          refreshPipelineById(Number(originNodeId));
        } else if (props.id) {
          // Otherwise refresh the main pipeline
          refreshPipelineById(Number(props.id));
        }

        // Optionally send acknowledgment back to parent
        if (event.source && 'postMessage' in event.source) {
          (event.source as Window).postMessage(
            {
              type: 'NEXLA_AGENT_RELOAD_COMPLETED',
              timestamp: new Date().toISOString(),
              flowId: props.id,
            },
            event.origin,
          );
        }
      };

      // Add postMessage listener
      window.addEventListener('message', handleAgentMessage);

      // Cleanup listener on unmount
      return () => {
        window.removeEventListener('message', handleAgentMessage);
      };
    }, [isEmbeddedMode, isFlowExpanded, originNodeId, props.id, refreshPipelineById]);

    const { createApiRequestNode } = useCreateApiRequest();
    const { openConfigModal } = useApiRequestModal();

    const prevDeps = useRef(null);
    const prevLayoutDirectionForMergeRef = useRef<typeof layoutDirection>(layoutDirection);

    /**
     * Main Effect: Node and Edge Creation Pipeline
     *
     * This is the core effect that orchestrates the entire node positioning pipeline.
     * It runs whenever key dependencies change and performs the following steps:
     *
     * Pipeline Steps:
     * 1. Prevent infinite loops with dependency comparison
     * 2. Create nodes from pipeline items (main flow)
     * 3. Add control nodes (flow control buttons)
     * 4. Create nodes for triggering flows (secondary flows)
     * 5. Create nodes for linked flows (secondary flows)
     * 6. Create edges for main flow
     * 7. Create edges for triggering flows
     * 8. Create edges for linked flows
     * 9. Apply Dagre layout algorithm
     * 10. Merge positions (preserve user modifications)
     * 11. Update React Flow state
     *
     * Dependencies:
     * - pipelineItems: Business data (sources, datasets, sinks, flows)
     * - expandedNodes: Which flows/nodes are expanded
     * - expandedSources: Which sources have expanded actions
     * - ragRequests: RAG-specific data
     * - isApiServerFlow: Whether this is an API server flow
     *
     * Note: This effect intentionally omits some dependencies (nodes, changedNodes, etc.)
     * to prevent infinite loops. The prevDeps comparison prevents unnecessary re-runs.
     *
     * @see docs/FlowNodesArchitecture.md - Data flow pipeline section
     */
    useEffect(() => {
      const currentDeps = {
        pipelineItems,
        expandedNodes,
        expandedSources,
        ragRequests,
        isApiServerFlow,
        isSqlAutomation,
        /**
         * ISSUE: Linked Flows Not Updating When Fetched
         *
         * Problem: When linked flows were fetched asynchronously, the flow canvas didn't
         * recalculate to include them. This happened because:
         * - linksMap was updated in Redux when linked flows were fetched
         * - But the main effect didn't have linksMap in its dependency array
         * - So the layout didn't recalculate when linked flows became available
         *
         * Solution: Add linksMap to the dependency array so the effect re-runs when
         * linked flows are fetched and the linksMap is updated.
         */
        linksMap,
        layoutDirection,
        branches,
        /**
         * ISSUE: BYO canvas shell nodes not rendering when GrowthBook resolves
         * after mount.
         *
         * Problem: nodeProps.byoV2Enabled (SourcePipeline.tsx) gates the BYO
         * create-flow SOURCE/CODE/DEST shell nodes in mappers.ts, but this
         * effect reads nodeProps only inside mapToNodes below — it wasn't in
         * currentDeps, so on a fresh page load (GrowthBook not yet ready at
         * first mount) the effect built the DAG once with byoV2Enabled=false
         * and never reran once the flag resolved true a render later.
         *
         * Solution: same fix as linksMap above — track it explicitly so the
         * effect reruns when the flag's resolved value changes.
         */
        byoV2Enabled: nodeProps?.byoV2Enabled,
        highlightedNodeKey: nodeProps?.highlightedNodeKey,
      };

      /**
       * Prevent infinite loops by comparing dependencies.
       *
       * This is critical because:
       * - The effect updates nodes/edges state
       * - State updates could trigger re-renders
       * - Re-renders could trigger this effect again
       *
       * By comparing dependencies, we only run when actual data changes,
       * not when state updates cause re-renders.
       */
      if (isEqual(prevDeps.current, currentDeps)) {
        return;
      }

      prevDeps.current = currentDeps;

      const layoutDirectionJustChanged = prevLayoutDirectionForMergeRef.current !== layoutDirection;

      /**
       * Initialize arrays for nodes and edges.
       * These will be populated by the mapping and edge creation functions.
       */
      const newNodes = [];
      const newEdges = [];
      const edgeLinksMap = {};

      /**
       * STEP 1: Create nodes for main flow
       *
       * Converts pipelineItems (business data) into React Flow Node objects.
       * This creates all resource nodes (sources, datasets, sinks) and flow container nodes.
       *
       * @see mappers.ts - mapToNodes function
       */
      mapToNodes(newNodes, {
        pipelineItems,
        entities,
        flowsOrder,
        keepFlowsOrder,
        aclQueryMode,
        isSecondaryFlow: false,
        isRagFlow: !!ragFlowId,
        isApiServerFlow,
        isSqlAutomation,
        layoutDirection,
        nodeProps: {
          ...nodeProps,
          linksMap,
          flow_triggers: Object.values(triggeringMap || {}),
          onClickOrchestrate,
          expandedNodes,
          expandedSources,
          toggleUseExpanded: handleOpenGenAiChat,
          layoutDirection,
          showScheduleCanvasHandles: true,
        },
      });

      /**
       * STEP 2: Add control nodes (flow control buttons)
       *
       * Control nodes are independent XYFlow nodes (Run Now, Cron Job) that
       * appear next to the source node and connect via edges.
       *
       * @see utils.ts - updateNodesWithControlNodes function
       */
      updateNodesWithControlNodes({
        pipelineItems,
        newNodes,
        newEdges,
        props,
        nodeProps,
        vendors,
        metaInfoById,
        layoutDirection,
        entities,
        branches,
      });

      /**
       * STEP 3: Create nodes for triggering flows (secondary flows)
       *
       * Triggering flows are flows that are triggered by completion of other flows.
       * These are displayed as secondary flows below the main flow.
       *
       * Note: isSecondaryFlow: true marks these nodes as secondary flow nodes,
       * which affects their positioning and display behavior.
       */
      if (triggeringMap && Object.keys(triggeringMap).length) {
        mapToNodes(newNodes, {
          pipelineItems: triggeringMap,
          entities,
          aclQueryMode,
          isSecondaryFlow: true,
          isRagFlow: !!ragFlowId,
          isApiServerFlow,
          isSqlAutomation,
          layoutDirection,
          nodeProps: {
            ...nodeProps,
            isSecondaryFlow: true,
            embedded: isEmbeddedMode,
            linksMap,
            onClickOrchestrate,
            expandedNodes,
            expandedSources,
            toggleUseExpanded: handleOpenGenAiChat,
            layoutDirection,
            showScheduleCanvasHandles: true,
          },
        });
      }

      /**
       * STEP 4: Create nodes for linked flows (secondary flows)
       *
       * Linked flows are flows that are connected to the main flow but not triggered.
       * These are also displayed as secondary flows below the main flow.
       *
       * Note: Both triggering and linked flows use isSecondaryFlow: true for consistent
       * positioning and behavior.
       */
      if (linksMap && Object.keys(linksMap).length) {
        mapToNodes(newNodes, {
          pipelineItems: linksMap,
          isSecondaryFlow: true,
          entities,
          aclQueryMode,
          isRagFlow: !!ragFlowId,
          isApiServerFlow,
          isSqlAutomation,
          layoutDirection,
          nodeProps: {
            ...nodeProps,
            isSecondaryFlow: true,
            embedded: isEmbeddedMode,
            linksMap,
            onClickOrchestrate,
            expandedNodes,
            expandedSources,
            toggleUseExpanded: handleOpenGenAiChat,
            layoutDirection,
            showScheduleCanvasHandles: true,
          },
        });
      }

      /**
       * STEP 5: Create edges for main flow
       *
       * Edges connect nodes based on data relationships. This creates edges
       * between resources in the main flow (source -> dataset -> sink).
       *
       * ISSUE: Edge Creation Race Condition
       *
       * Problem: Edges were being created before their target nodes existed, causing
       * edges with invalid targets like 'node-undefined' or edges that failed to render.
       *
       * Solution: Track existing node IDs and pass them to createEdges() so it can
       * validate that both source and target nodes exist before creating edges.
       *
       * @see createEdges.ts - createEdges function
       */
      const existingNodeIds = new Set(newNodes.map((n) => n.id));
      createEdges(pipelineItems, newEdges, {
        metaInfoById,
        edgesLinkMap: edgeLinksMap,
        isSecondaryFlow: false,
        nodeProps: {
          ...nodeProps,
          linksMap,
          expandedNodes,
          expandedSources,
        },
        ragRequests,
        entities,
        vendors,
        canvasScheduleEdgesEnabled: true,
        scheduleCanvasFlowRootId: props.id,
        layoutDirection,
        existingNodeIds,
      });

      /**
       * STEP 6: Create edges for triggering flows
       *
       * Creates edges between nodes in triggering flows (secondary flows).
       * These edges connect resources within each triggering flow.
       */
      if (triggeringMap) {
        createEdges(triggeringMap, newEdges, {
          metaInfoById,
          edgesLinkMap: edgeLinksMap,
          isSecondaryFlow: true,
          nodeProps: {
            ...nodeProps,
            linksMap,
            onClickOrchestrate,
            expandedNodes,
          },
          ragRequests,
          entities,
          vendors,
          canvasScheduleEdgesEnabled: true,
          scheduleCanvasFlowRootId: props.id,
          layoutDirection,
          existingNodeIds,
        });
      }

      /**
       * STEP 7: Create edges for linked flows
       *
       * Creates edges between nodes in linked flows (secondary flows).
       * Similar to triggering flows, but for linked flow connections.
       */
      if (linksMap) {
        createEdges(linksMap, newEdges, {
          metaInfoById,
          edgesLinkMap: edgeLinksMap,
          isSecondaryFlow: true,
          nodeProps: {
            ...nodeProps,
            linksMap,
            onClickOrchestrate,
            expandedNodes,
          },
          ragRequests,
          entities,
          vendors,
          canvasScheduleEdgesEnabled: true,
          scheduleCanvasFlowRootId: props.id,
          layoutDirection,
          existingNodeIds,
        });
      }

      /**
       * STEP 8: Apply Dagre layout algorithm
       *
       * This is where automatic positioning happens. The Dagre algorithm:
       * - Groups nodes by parentId (nested flows)
       * - Calculates optimal positions based on node sizes and edges
       * - Positions special nodes (credentials, nexset loaders) relative to parents
       * - Positions secondary flows below main flow
       *
       * Note: We create a copy of newNodes ([...newNodes]) because Dagre mutates
       * the nodes array. This prevents issues if we need the original positions.
       *
       * @see dagreLayout.ts - applyDagreLayoutToResources function
       */
      // Extract flow type from flowNodes entity or pipelineItems
      let flowType: string | undefined;
      if (props.id && flowNodes[props.id]) {
        flowType = flowNodes[props.id]?.flow_type;
      }
      if (!flowType) {
        // flowNodes entity may not carry flow_type (e.g. BYO); check pipelineItems which
        // always includes the source entity carrying flow_type: 'custom'.
        const flowItem = Object.values(pipelineItems).find(
          (item: any) => item.resource_type === 'flow' || item.flow_type,
        ) as any;
        flowType = flowItem?.flow_type;
      }

      const laidOutNodes = applyDagreLayoutToResources([...newNodes], newEdges, {
        isRagFlow: !!ragFlowId,
        flowType,
        vendors,
        metaInfoById,
        rankdir: layoutDirection,
        byoV2Enabled: nodeProps?.byoV2Enabled,
      });

      /**
       * STEP 9: Merge computed positions with user-modified positions
       *
       * This preserves positions for nodes that users have manually moved.
       * When layout direction just changed (LR <-> TB), we skip preservation
       * and use all Dagre-computed positions so the full layout rearranges.
       *
       * @see helpers.ts - mergeNodesPosition function
       */
      if (layoutDirectionJustChanged) {
        prevLayoutDirectionForMergeRef.current = layoutDirection;
      }
      const mergedNodes = mergeNodesPosition(
        nodes,
        laidOutNodes as Node<{ item: any; expanded: boolean }>[],
        changedNodes,
        newEdges,
        layoutDirectionJustChanged ? { useComputedPositionsOnly: true } : undefined,
      );

      /**
       * STEP 9.5: Hide child nodes when their parent flow is collapsed
       *
       * When a flow container is collapsed (not in expandedNodes), we need to hide
       * all its child nodes (resources inside the flow) so only the flow container
       * itself is visible.
       *
       * This prevents the bug where collapsed flows still show their internal
       * source/dataset/sink nodes.
       *
       * Note: RAG flow nodes (like data_retriever_holder) should not be hidden
       * based on parent flow expansion, as they are part of the main flow structure.
       */
      mergedNodes.forEach((node) => {
        // Check if this node has a parent (is a child of a flow container)
        if (node.parentId) {
          // Skip hiding for RAG flow nodes - they're part of the main flow, not nested flows
          const isRagFlowNode =
            node.type === 'data_retriever_holder' ||
            node.type === 'query_prompt' ||
            node.type === 'query_rewriter' ||
            node.type === 'context_enricher' ||
            node.type === 'llm_processor' ||
            node.type === 'reranker' ||
            node.type === 'query_response';

          if (isRagFlowNode) {
            // RAG flow nodes should never be hidden based on parent expansion
            return;
          }

          // Check if parent is a data_retriever_holder - children inside should always be visible
          const parentNode = mergedNodes.find((n) => n.id === node.parentId);
          if (parentNode?.type === 'data_retriever_holder') {
            // Nodes inside retriever holders should always be visible
            // (retriever holders are not collapsible flow containers)
            return;
          }

          // Hide the node if its parent flow is not expanded
          const parentExpanded = expandedNodes.includes(node.parentId);
          node.hidden = !parentExpanded;
        }
      });

      /**
       * STEP 10: Filter edges to only include those with valid source and target nodes
       *
       * ISSUE: Edge Rendering Race Condition (continued)
       *
       * Problem: Even after passing existingNodeIds to createEdges(), some edges might
       * still have invalid targets due to:
       * - Recursive edge creation in nested flows
       * - Asynchronous node creation in linked flows
       * - Edge creation happening before all nodes are added to mergedNodes
       *
       * Solution: Final validation pass - filter out any edges that don't have both
       * source and target nodes in the final mergedNodes array. This is a safety net
       * to ensure we never set edges with invalid targets.
       */
      const nodeIds = new Set(mergedNodes.map((n) => n.id));
      const validEdges = newEdges.filter((edge) => {
        const hasSource = nodeIds.has(edge.source);
        const hasTarget = nodeIds.has(edge.target);
        if (!hasSource || !hasTarget) {
          // Log warning for debugging, but don't spam console
          if (process.env.NODE_ENV === 'development') {
            console.warn(`[FlowNodes] Filtering edge ${edge.id}: source or target node missing`, {
              source: edge.source,
              target: edge.target,
              hasSource,
              hasTarget,
            });
          }
          return false;
        }
        return true;
      });

      /**
       * STEP 11: Update React Flow state
       *
       * ISSUE: Edge Rendering Race Condition (final step)
       *
       * Problem: Setting nodes and edges together in the same render cycle can cause
       * edges to be rendered before their target nodes are in the DOM, even if the nodes
       * exist in the state.
       *
       * Solution: Two-phase update:
       * 1. Set nodes first (this triggers React Flow to render them)
       * 2. Store edges in a ref to be set in a separate effect (see effect below)
       *    This ensures nodes are fully rendered before edges are added
       */
      /**
       * GUARD: Prevent StoreUpdater update loops
       *
       * In controlled ReactFlow, repeatedly calling setNodes with an equivalent nodes array
       * can trigger ReactFlow's internal StoreUpdater to keep syncing and re-rendering.
       * PERFORMANCE OPTIMIZATION: we compare the fields that matter for ReactFlow updates:
       * id, position, hidden, type, data.
       */
      const nodesEqual =
        mergedNodes.length === nodes.length &&
        mergedNodes.every((newNode, i) => {
          const oldNode = nodes[i];
          if (!oldNode || newNode.id !== oldNode.id) return false;
          // Compare critical fields that affect ReactFlow rendering (including handle positions for layout toggle)
          return (
            newNode.position.x === oldNode.position.x &&
            newNode.position.y === oldNode.position.y &&
            newNode.hidden === oldNode.hidden &&
            newNode.type === oldNode.type &&
            newNode.sourcePosition === oldNode.sourcePosition &&
            newNode.targetPosition === oldNode.targetPosition &&
            isEqual(newNode.data, oldNode.data)
          );
        });

      if (!nodesEqual) {
        setNodes(mergedNodes);
        pendingEdgesRef.current = validEdges;

        // Only sync handle internals when layout direction changes.
        // Do NOT call updateNodeInternals for new control nodes on initial render —
        // it causes visible jitter by triggering ReactFlow re-measurement.
        // Edges will connect via the pending edges mechanism after nodes are in DOM.
        if (layoutDirectionJustChanged) {
          if (handleUpdateTimerRef.current) clearTimeout(handleUpdateTimerRef.current);
          const ids = mergedNodes.map((n) => n.id);
          const isTBTarget = layoutDirection === 'TB';
          handleUpdateTimerRef.current = setTimeout(() => {
            ids.forEach((id) => updateNodeInternals(id));
            // Locked zoom + pin the content bbox to the leading edge of the
            // viewport. Container nodes (flow / secondary_flow) size to
            // worst-case branching width, so we exclude them from the bbox.
            placeFlowInViewport(flowInstance, mergedNodes, isTBTarget, {
              viewportWidth: flowElementRef.current?.clientWidth ?? 0,
              viewportHeight: flowElementRef.current?.clientHeight ?? 0,
              duration: 400,
              fallback: () =>
                fitView({
                  duration: 400,
                  padding: 0.2,
                  maxZoom: 0.9,
                  minZoom: 0.9,
                }),
            });
            handleUpdateTimerRef.current = null;
          }, 120);
        }
      } else {
        // Nodes are already rendered; update edges immediately (still guarded).
        const edgesEqual = isEqual(validEdges, lastEdgesRef.current);
        if (!edgesEqual) {
          isSettingEdgesRef.current = true;
          setEdges(validEdges);
          lastEdgesRef.current = validEdges;
          isSettingEdgesRef.current = false;
        }
        pendingEdgesRef.current = [];
      }
    }, [
      pipelineItems,
      expandedNodes,
      expandedSources,
      ragRequests,
      isApiServerFlow,
      isSqlAutomation,
      linksMap,
      layoutDirection,
      branches,
      // See currentDeps.byoV2Enabled comment above — this is the actual React
      // dependency array; the currentDeps object alone doesn't get the effect
      // to re-run without this entry too.
      nodeProps?.byoV2Enabled,
    ]);

    /**
     * Effect: Set edges after nodes are confirmed to be set
     *
     * ISSUE: Edge Rendering Race Condition (deferred edge setting)
     *
     * Problem: Setting edges in the same effect as nodes can cause edges to be rendered
     * before their target nodes are in the DOM, even if nodes exist in state.
     *
     * Solution: This effect runs after nodes are set and validates that:
     * 1. All edge targets exist in the nodes array
     * 2. Edges are different from the last set (prevents unnecessary updates)
     * 3. We're not already setting edges (prevents re-entry/infinite loops)
     *
     * IMPORTANT: This effect includes guards to prevent infinite loops:
     * - isSettingEdgesRef prevents re-entry
     * - Edge comparison (isEqual) prevents setting identical edges
     * - Only runs when pendingEdgesRef has edges to set
     *
     * The edge comparison is critical because ReactFlow may update nodes internally,
     * which would trigger this effect again. By comparing edges, we avoid setting
     * the same edges multiple times.
     */
    useEffect(() => {
      // Prevent re-entry to avoid infinite loops
      if (isSettingEdgesRef.current) {
        return;
      }

      if (pendingEdgesRef.current.length > 0 && nodes.length > 0) {
        // Verify all edge targets exist in nodes before setting edges
        const nodeIds = new Set(nodes.map((n) => n.id));
        const validEdges = pendingEdgesRef.current.filter((edge) => {
          return nodeIds.has(edge.source) && nodeIds.has(edge.target);
        });

        // Compare with last edges to avoid unnecessary updates
        // This prevents infinite loops when ReactFlow updates nodes internally
        // Use isEqual for deep comparison to catch all edge property changes
        const edgesEqual = isEqual(validEdges, lastEdgesRef.current);

        if (!edgesEqual) {
          isSettingEdgesRef.current = true;
          setEdges(validEdges);
          lastEdgesRef.current = validEdges;
          // Clear pending edges after setting
          pendingEdgesRef.current = [];
          // Reset flag - edge comparison will prevent infinite loops
          isSettingEdgesRef.current = false;
        } else {
          // Edges are the same, just clear pending edges
          pendingEdgesRef.current = [];
        }
      }
    }, [nodes, setEdges]);

    /**
     * Callback: Handle node changes (drag, position updates, etc.)
     *
     * This callback is called by React Flow whenever nodes change (user drags,
     * positions update, etc.). We use it to:
     * 1. Prevent node deletion (not allowed)
     * 2. Track user-moved nodes for position preservation
     * 3. Apply changes to React Flow state
     *
     * Position Tracking:
     * - When user finishes dragging a node (change.dragging === false), we track it
     * - The tracked position is used by mergeNodesPosition to preserve user modifications
     * - We also track the flowNode to identify which flow container the node belongs to
     *
     * @param changes - Array of node changes from React Flow
     *
     * @see mergeNodesPosition - Uses changedNodes to preserve positions
     *
     * TODO: Add flowNodes to dependency array to fix stale closure
     */
    const onNodesChange = useCallback(
      (changes: NodeChange<Node>[]) => {
        // Not allowing to remove a node
        if (changes.some((change) => change.type === 'remove')) return;

        /**
         * ISSUE: Infinite Loop in Node Position Tracking
         *
         * Problem: Without this guard, the callback could be called recursively:
         * - User drags node → onNodesChange fires
         * - We call setNodes() → React Flow updates internally
         * - React Flow triggers onNodesChange again → infinite loop
         *
         * Solution: Use a ref flag to prevent re-entry. The flag is reset after React
         * processes the state update (using setTimeout to ensure it's after the render cycle).
         */
        if (isProcessingNodesChangeRef.current) {
          return;
        }

        isProcessingNodesChangeRef.current = true;

        try {
          /**
           * Batch position tracking to prevent multiple re-renders.
           *
           * ISSUE: Multiple Re-renders from Position Updates
           *
           * Problem: If multiple nodes are moved, calling setChangedNodes() for each
           * would cause multiple re-renders, which is inefficient.
           *
           * Solution: Collect all position changes first, then update state once.
           */
          const positionChangesToTrack: Array<{
            id: string;
            flowNode: any;
            position: XYPosition;
          }> = [];

          changes.forEach((change) => {
            /**
             * Track position changes when user finishes dragging.
             *
             * We only track when dragging is false (user released the node) because:
             * - During dragging, position changes are temporary
             * - We only want to preserve the final position where user dropped the node
             */
            if (change.type === 'position' && !change.dragging) {
              let flowKey = change.id;
              if (flowKey.startsWith('run-now-')) {
                flowKey = flowKey.slice('run-now-'.length);
              } else if (flowKey.startsWith('cron-')) {
                flowKey = flowKey.slice('cron-'.length);
              } else {
                flowKey = flowKey.replace(/^node-/, '');
              }
              const flowNode = flowNodes[flowKey];
              positionChangesToTrack.push({
                id: change.id,
                flowNode,
                position: change.position,
              });
            }
          });

          // Batch update changedNodes to prevent multiple re-renders
          if (positionChangesToTrack.length > 0) {
            setChangedNodes((changedNodes) => {
              const updated = { ...changedNodes };
              positionChangesToTrack.forEach(({ id, flowNode, position }) => {
                updated[id] = { flowNode, position };
              });
              return updated;
            });
          }

          // Apply the changes to React Flow state (this updates the visual position)
          // React Flow requires us to apply all changes, including during drag
          setNodes((nds) => applyNodeChanges(changes, nds));
        } finally {
          /**
           * Reset the flag after React has processed the state update.
           *
           * Using setTimeout ensures the flag is reset in the next event loop tick,
           * after React has finished processing the state update. This prevents
           * the flag from being reset too early, which could allow re-entry.
           */
          setTimeout(() => {
            isProcessingNodesChangeRef.current = false;
          }, 0);
        }
      },
      [flowNodes],
    );

    const onEdgesChange = useCallback((changes: any) => {
      // Not allowing to remove an edge
      if (changes.some((change: any) => change.type === 'remove')) return;
      setEdges((eds) => applyEdgeChanges(changes, eds));
    }, []);

    // Handle drag start from source handle to capture source info
    const handleConnectStart = useCallback((event: any, { nodeId, handleId }: any) => {
      dragSourceRef.current = { nodeId, handleId };
    }, []);

    const onNodeMouseEnter = useCallback(
      (_event: React.MouseEvent, node: Node) => {
        if (!isZoomedOut) return;
        setHoveredNodeId(node.id);
      },
      [isZoomedOut],
    );

    const onNodeMouseLeave = useCallback(
      (_event: React.MouseEvent, node: Node) => {
        if (!isZoomedOut) return;
        setHoveredNodeId(null);
      },
      [isZoomedOut],
    );

    const originalZIndexRef = useRef<Map<string, number>>(new Map());

    useEffect(() => {
      if (!isZoomedOut) {
        if (hoveredNodeId !== null) {
          setHoveredNodeId(null);
        }
        // Only call setNodes if there are actually zIndex values to restore
        if (originalZIndexRef.current.size > 0) {
          setNodes((nds) =>
            nds.map((node) => {
              const originalZIndex = originalZIndexRef.current.get(node.id);
              if (originalZIndex !== undefined && node.zIndex !== originalZIndex) {
                return { ...node, zIndex: originalZIndex };
              }
              return node;
            }),
          );
        }
        return;
      }

      setNodes((nds) =>
        nds.map((node) => {
          if (!originalZIndexRef.current.has(node.id)) {
            originalZIndexRef.current.set(node.id, node.zIndex || Z_INDEX.RESOURCE);
          }

          const shouldElevate = node.id === hoveredNodeId;
          const originalZIndex = originalZIndexRef.current.get(node.id) || Z_INDEX.RESOURCE;

          if (shouldElevate) {
            if (node.zIndex === Z_INDEX.FLOW_CANVAS_OVERLAY) {
              return node;
            }
            return {
              ...node,
              zIndex: Z_INDEX.FLOW_CANVAS_OVERLAY,
            };
          } else {
            if (node.zIndex === originalZIndex) {
              return node;
            }
            return {
              ...node,
              zIndex: originalZIndex,
            };
          }
        }),
      );
    }, [hoveredNodeId, isZoomedOut, setNodes]);

    // Handle drag end from source handle to create a new node
    const handleConnectEnd = useCallback(
      async (event: MouseEvent | TouchEvent) => {
        // Get the target element from the event
        const target = event.target as HTMLElement;
        const isHandle = target?.closest('.react-flow__handle');
        const isNode = target?.closest('.react-flow__node');

        if (!isNode || !isHandle) {
          if (!dragSourceRef.current) {
            return;
          }
          const flowNodeId = dragSourceRef.current.nodeId.replace('node-', '');
          const flowNodeData = flowNodes[flowNodeId];

          if (!flowNodeData) {
            return;
          }

          if (flowNodeData.node_type === 'api_server') {
            try {
              // Extract method from handleId (e.g., 'get-handle' -> 'GET')
              const method = dragSourceRef.current.handleId.split('-')[0].toUpperCase();
              const newNodeId = await createApiRequestNode(flowNodeData, method);

              // Open the config modal with the same configuration
              openConfigModal(newNodeId, flowNodeData?.id, method);
            } catch (error) {
              console.error('Failed to create API request node:', error);
            }
            return;
          }

          // Node types that should show the select node menu
          const allowedNodeTypes = [
            'api_result',
            'api_request',
            'api_success_modifier',
            'splitter_output',
            'api_error',
            'api_error_modifier',
          ];

          if (!allowedNodeTypes.includes(flowNodeData.node_type)) {
            return;
          }

          const clientX = 'clientX' in event ? event.clientX : event.touches[0].clientX;
          const clientY = 'clientY' in event ? event.clientY : event.touches[0].clientY;

          const position = screenToFlowPosition({
            x: clientX,
            y: clientY,
          });

          const newTempNode: Node = {
            id: 'temp-target',
            type: 'virtual',
            position,
            data: {},
            style: { opacity: 0 },
          };

          const newTempEdge: Edge = {
            id: 'temp-edge',
            source: dragSourceRef.current.nodeId,
            target: 'temp-target',
            sourceHandle: dragSourceRef.current.handleId,
            targetHandle: 'left',
            type: 'bezier',
            style: {
              stroke: 'var(--color-greyscale-200)',
              strokeWidth: 2,
            },
            animated: true,
          };

          setTempNode(newTempNode);
          setTempEdge(newTempEdge);
          setNodes((nodes) => [...nodes, newTempNode]);
          setEdges((edges) => [...edges, newTempEdge]);

          setContextMenu({
            position: { x: clientX, y: clientY },
            sourceHandle: dragSourceRef.current.handleId,
            sourceNode: dragSourceRef.current.nodeId,
          });
        }
        dragSourceRef.current = null;
      },
      [flowNodes, screenToFlowPosition, setNodes, setEdges, createApiRequestNode, openConfigModal],
    );

    const handleMenuClose = useCallback(() => {
      setContextMenu(null);

      // Clean up temporary items
      if (tempNode && tempEdge) {
        setNodes((nodes) => nodes.filter((n) => n.id !== tempNode.id));
        setEdges((edges) => edges.filter((e) => e.id !== tempEdge.id));
        setTempNode(null);
        setTempEdge(null);
      }
    }, [tempNode, tempEdge, setNodes, setEdges]);

    // Use shared hook for node creation actions
    const { createTransform, createTarget, getSplitParentDataSet } = useNodeCreationActions({
      pipelineId: Number(props.id),
    });

    const [splitParentDataSet, setSplitParentDataSet] = useState<any>(null);

    const handleCreateTransform = useCallback(() => {
      if (!contextMenu) return;
      const sourceNodeId = contextMenu.sourceNode.replace('node-', '');
      const sourceNodeData = flowNodes[sourceNodeId];
      if (!sourceNodeData) {
        setContextMenu(null);
        return;
      }
      createTransform(sourceNodeData);
    }, [contextMenu, flowNodes, createTransform]);

    const handleCreateTarget = useCallback(() => {
      if (!contextMenu) return;
      const sourceNodeId = contextMenu.sourceNode.replace('node-', '');
      const sourceNodeData = flowNodes[sourceNodeId];
      if (!sourceNodeData) {
        setContextMenu(null);
        return;
      }
      createTarget(sourceNodeData);
    }, [contextMenu, flowNodes, createTarget]);

    const handleCreateSplit = useCallback(() => {
      if (!contextMenu) return;
      const sourceNodeId = contextMenu.sourceNode.replace('node-', '');
      const sourceNodeData = flowNodes[sourceNodeId];
      if (!sourceNodeData) {
        setContextMenu(null);
        return;
      }
      setSplitParentDataSet(getSplitParentDataSet(sourceNodeData));
    }, [contextMenu, flowNodes, getSplitParentDataSet]);

    const canCreateSplit =
      contextMenu &&
      (() => {
        const sourceNodeId = contextMenu.sourceNode.replace('node-', '');
        const sourceNodeData = flowNodes[sourceNodeId];
        return (
          sourceNodeData?.type === 'DATA_SET' &&
          sourceNodeData.node_type !== 'splitter' &&
          !sourceNodeData.children?.length
        );
      })();

    const updatedMinMapStyle = useMemo(() => {
      return {
        ...minMapDefaultStyle,
        ...miniMapStyle,
      };
    }, [miniMapStyle]);

    const sourceContext = useMemo(() => {
      const dataSourceId = flowNodes[props.id]?.data_source_id ?? nodeProps?.flow?.data_source_id;
      const source = entities?.sources?.[dataSourceId];
      const sourceType = source?.source_type;
      const connectionCategory =
        source?.connection_type && metaInfoById
          ? metaInfoById[source.connection_type]?.connectionCategory
          : undefined;
      return { sourceType, connectionCategory };
    }, [props.id, nodeProps?.flow, flowNodes, entities.sources, metaInfoById]);

    const showMetricsSelector = useMemo(() => {
      if (nodeProps?.hideMetricsSelector) return false;
      // The 24h Data Flow Status view renders its node numbers from a dedicated
      // dashboard API (state.metrics.dashboard via fetchDashboardMetrics), which
      // this toggle does not drive — so the control would be inert there. Hide it.
      // NOTE: FlowsTable reuses this same preview WITHOUT isDataFlowStatus, so it
      // keeps the toggle (its numbers do come from the interval-driven fetch).
      if (nodeProps?.isDataFlowStatus) return false;
      const flowType = flowNodes[props.id]?.flow_type ?? nodeProps?.flow?.flow_type;
      if (isExcludedFlowType(flowType)) return false;
      if (isSqlAutomation === true) return false;

      return true;
    }, [
      props.id,
      nodeProps?.flow,
      nodeProps?.hideMetricsSelector,
      nodeProps?.isDataFlowStatus,
      flowNodes,
      isSqlAutomation,
    ]);

    const selectorOptions = getMetricsIntervalOptions(sourceContext);

    const effectiveInterval = resolveEffectiveInterval(sourceContext, metricsInterval);

    // Single source of truth for per-node metrics: fetch honoring the currently
    // effective interval (batch → run_id/lifetime, continuous → last24h/lifetime,
    // uncategorized → lifetime). Used both on mount/interval-change (effect below)
    // and on the toolbar Refresh button, so refresh re-fetches like the first load.
    const fetchFlowMetrics = useCallback(() => {
      if (nodeProps?.isPreview) return;
      const flow = nodeProps?.flow;
      const originNodeId = flow?.id;
      const sourceId = flow?.data_source_id;
      if (originNodeId == null || !sourceId) return;
      requestFlowMetrics(originNodeId, sourceId, { interval: effectiveInterval });
    }, [
      nodeProps?.isPreview,
      nodeProps?.flow?.id,
      nodeProps?.flow?.data_source_id,
      effectiveInterval,
      requestFlowMetrics,
    ]);

    useEffect(() => {
      fetchFlowMetrics();
    }, [fetchFlowMetrics]);

    const onChangeInterval = useCallback(
      (interval: string) => {
        // Only update state. The fetch is driven by the effect above, which depends on
        // `effectiveInterval` — so changing the interval re-fetches exactly once. Issuing a
        // request here too would double-fetch now that the effect reacts to the interval.
        dispatch(setFlowMetricsInterval(interval));
      },
      [dispatch],
    );

    return (
      <ExternalTriggerProvider>
        <ReactFlow
          ref={flowElementRef}
          className={classNames(classes.container, {
            [classes.pendingFit]: !fitted,
            [classes.embeddedCanvas]: isEmbeddedMode,
          })}
          data-layout-direction={layoutDirection}
          edgesReconnectable={false}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnectStart={handleConnectStart}
          onConnectEnd={handleConnectEnd}
          onNodeMouseEnter={onNodeMouseEnter}
          onNodeMouseLeave={onNodeMouseLeave}
          connectionLineComponent={AnimatedConnectionLine}
          fitViewOptions={fitViewOptions}
          minZoom={0.2}
          maxZoom={1.2}
          panOnScroll
          zoomOnScroll={false}
          proOptions={proOption}
          edgeTypes={edgeTypes}
          nodeTypes={types}
          style={
            {
              ...(mode === 'dark' &&
                isEmbeddedMode && {
                  '--flow-nodes-canvas-bg': '#2a2a2a',
                  '--schedule-subtext-color': 'var(--color-greyscale-0)',
                  '--schedule-subtext-hover-color': 'var(--color-primary-200)',
                }),
            } as React.CSSProperties
          }
        >
          <Background />
          {!nodeProps?.isPreview && !hideMiniMap ? (
            <MiniMap
              pannable
              style={updatedMinMapStyle}
              maskStrokeWidth={1}
              maskStrokeColor="var(--color-greyscale-400)"
              nodeColor="var(--color-greyscale-200)"
              maskColor="rgba(255,255,255,0.7)"
              position={miniMapPosition}
            />
          ) : null}
          <Controls
            style={
              isEmbeddedMode
                ? {
                    width: 'max-content',
                    maxWidth: 'none',
                    overflow: 'visible',
                    ...(mode === 'dark' ? { backgroundColor: 'var(--color-greyscale-100)' } : {}),
                  }
                : {}
            }
            position={isEmbeddedMode ? 'bottom-right' : 'bottom-left'}
            orientation="horizontal"
          >
            {props.toolbarItems?.map((item: ToolbarItem) => (
              <ControlButton
                key={item.title}
                title={item.title}
                onClick={() => {
                  if (typeof item.onClick === 'function') {
                    item.onClick(props.id);
                  }
                  // On Refresh, the parent handler (`item.onClick`) reloads the flow
                  // STRUCTURE; FlowNodes owns the per-node METRICS re-fetch here,
                  // honoring the selected interval via `fetchFlowMetrics` (no-ops in
                  // preview mode, where the parent's own refresh drives metrics).
                  // This is the single metrics path on the canvas — parent refresh
                  // handlers (PipelineForm, EditBaseFlow, Pipelines list) must NOT
                  // also fetch metrics, or Refresh double-fires (NEX-21823).
                  if (item.title === 'Refresh') {
                    fetchFlowMetrics();
                  }
                }}
              >
                {item.Icon}
              </ControlButton>
            ))}
            {showMetricsSelector ? (
              <div className={classes.metricsIntervalSelectorWrapper}>
                <MetricsIntervalSelector
                  options={selectorOptions}
                  value={effectiveInterval}
                  onChange={onChangeInterval}
                />
              </div>
            ) : null}
          </Controls>

          {isGenAiChatOpen && (
            <DraggableGenAiChatNode
              id="genAiChat"
              data={{
                onClose: handleCloseGenAiChat,
                onStreamComplete: () => {
                  if (isFlowExpanded) {
                    refreshPipelineById(Number(originNodeId));
                    return;
                  }

                  refreshPipelineById(Number(props.id));
                  return;
                },
              }}
              nexsetId={selectedNexsetIdForGenAiChat}
              isFlowExpanded={isFlowExpanded}
            />
          )}
          {children}
          <ExternalTriggerUsage />
          <HandleMenu
            isOpen={Boolean(contextMenu)}
            position={
              contextMenu
                ? {
                    x: contextMenu.position.x,
                    y: contextMenu.position.y,
                  }
                : null
            }
            onClose={handleMenuClose}
            onTransform={handleCreateTransform}
            onTarget={handleCreateTarget}
            onSplit={canCreateSplit ? handleCreateSplit : undefined}
          />
          {splitParentDataSet && (
            <DataSetSplitDialog
              parentDataSet={splitParentDataSet}
              onClose={() => setSplitParentDataSet(null)}
            />
          )}
        </ReactFlow>
      </ExternalTriggerProvider>
    );
  },
);

const arePropsEquals = (prevProps: FlowNodesProps, nextProps: FlowNodesProps): boolean => {
  const { pipelineItems: prevPipelineItems, ...restPrev } = prevProps;
  const { pipelineItems: nextPipelineItems, ...restNext } = nextProps;

  const isPipelineItemsEqual = isEqual(prevPipelineItems, nextPipelineItems);

  if (isPipelineItemsEqual) return isEqual(restPrev, restNext);

  return false;
};

export const FlowNodes = memo(
  forwardRef((props: PropsWithChildren<FlowNodesProps>, ref) => {
    const { refreshPipelineById } = usePipelinesAdapter();

    const onModalComplete = useCallback(() => {
      refreshPipelineById(Number(props.id));
    }, [props.id, refreshPipelineById]);

    return (
      <ResourceEditProvider onComplete={onModalComplete}>
        <ApiRequestModalProvider>
          <ApiServerHelpProvider>
            <ReactFlowProvider>
              <ExpandHandlerProvider>
                <ResourceOrchestrationProvider>
                  <ResourceDetailProvider>
                    <FlowNodesInternal ref={ref} {...props} />
                  </ResourceDetailProvider>
                </ResourceOrchestrationProvider>
              </ExpandHandlerProvider>
            </ReactFlowProvider>
          </ApiServerHelpProvider>
        </ApiRequestModalProvider>
      </ResourceEditProvider>
    );
  }),
  arePropsEquals,
);
